export type ProjectStatus = "Planning" | "In Progress" | "Complete";
export type TodoStatus = "Not Started" | "In Progress" | "Blocked" | "Done";
export type SupplyStatus = "Needed" | "Ordered" | "Purchased" | "Delivered" | "Installed";
export type PermitStatus = "Needed" | "Drafting" | "Submitted" | "Approved" | "Rejected" | "Closed";
export type InteractionStatus = "Scheduled" | "Completed" | "Cancelled";
export type InteractionType =
  | "Consultation"
  | "Estimate"
  | "Work Visit"
  | "Inspection"
  | "Follow-up"
  | "Walkthrough";
export type QuoteStatus = "Requested" | "Received" | "Accepted" | "Declined" | "Expired";
export type InvoiceStatus = "Received" | "Due" | "Paid" | "Overdue" | "Cancelled";
export type MaintenanceStatus = "Open" | "Scheduled" | "Complete";
export type MaintenanceFrequency = "One-time" | "Monthly" | "Quarterly" | "Yearly" | "Custom";

export type AuditMetadata = {
  createdAt?: string;
  createdBy?: string;
  updatedAt?: string;
  updatedBy?: string;
  deletedAt?: string;
  deletedBy?: string;
};

export type ProjectTodo = AuditMetadata & {
  id: string;
  projectId: string;
  title: string;
  status: TodoStatus;
  dueDate?: string;
};

export type Permit = AuditMetadata & {
  id: string;
  projectId: string;
  title: string;
  status: PermitStatus;
  submissionDueDate?: string;
  approvalDeadline?: string;
};

export type ProfessionalInteraction = AuditMetadata & {
  id: string;
  projectId: string;
  professionalId?: string;
  title: string;
  status: InteractionStatus;
  startsAt: string;
  type: InteractionType;
};

export type Invoice = AuditMetadata & {
  id: string;
  projectId: string;
  title: string;
  amount: number;
  status: InvoiceStatus;
  dueDate?: string;
};

export type ProjectSupply = AuditMetadata & {
  id: string;
  projectId: string;
  itemName: string;
  quantity: number;
  status: SupplyStatus;
  estimatedCost?: number;
  actualCost?: number;
};

export type ProjectQuote = AuditMetadata & {
  id: string;
  projectId: string;
  title: string;
  amount: number;
  status: QuoteStatus;
};

export type MaintenanceItem = AuditMetadata & {
  id: string;
  title: string;
  status: MaintenanceStatus;
  frequency: MaintenanceFrequency;
  nextDueDate?: string;
};

export type HouseholdScheduleSources = {
  todos?: ProjectTodo[];
  permits?: Permit[];
  interactions?: ProfessionalInteraction[];
  invoices?: Invoice[];
  maintenance?: MaintenanceItem[];
};

export type MaintenanceRecurrenceInput = {
  completedOn: string;
  frequency: MaintenanceFrequency;
  customIntervalDays?: number;
};

export type ProjectCostInput = {
  budget?: number | undefined;
  supplies?: ProjectSupply[];
  quotes?: ProjectQuote[];
  invoices?: Invoice[];
};

export type ProjectCostRollup = {
  estimatedSupplyTotal: number;
  actualSupplyTotal: number;
  quoteTotal: number;
  invoiceTotal: number;
  actualCost: number;
  variance: number | undefined;
};

export type DatedItemType = "todo" | "permit" | "interaction" | "invoice" | "maintenance";

export type DatedHouseholdItem = {
  id: string;
  sourceId: string;
  type: DatedItemType;
  title: string;
  date: string;
  projectId?: string;
};

export type DateRange = {
  from: string;
  to: string;
};

export type AttachmentTarget =
  | "project"
  | "supply"
  | "permit"
  | "interaction"
  | "quote"
  | "invoice"
  | "maintenance";

export type AttachmentKeyInput = {
  householdId: string;
  entityType: string;
  entityId: string;
  filename: string;
  uniqueId: string;
};

function isDeleted(record: AuditMetadata): boolean {
  return Boolean(record.deletedAt);
}

function localDate(value: string): string {
  return value.slice(0, 10);
}

function compareDatedItems(left: DatedHouseholdItem, right: DatedHouseholdItem): number {
  const dateOrder = localDate(left.date).localeCompare(localDate(right.date));
  if (dateOrder !== 0) {
    return dateOrder;
  }

  const typeOrder: Record<DatedItemType, number> = {
    todo: 1,
    permit: 2,
    interaction: 3,
    invoice: 4,
    maintenance: 5,
  };
  const categoryOrder = typeOrder[left.type] - typeOrder[right.type];
  if (categoryOrder !== 0) {
    return categoryOrder;
  }

  return left.sourceId.localeCompare(right.sourceId);
}

function isDueOrOverdue(date: string, today: string): boolean {
  return localDate(date) <= today;
}

export function getNeedsAttention(sources: HouseholdScheduleSources, today: string): DatedHouseholdItem[] {
  const items: DatedHouseholdItem[] = [];

  for (const todo of sources.todos ?? []) {
    if (!todo.dueDate || todo.status === "Done" || isDeleted(todo) || !isDueOrOverdue(todo.dueDate, today)) {
      continue;
    }

    items.push({
      id: `todo:${todo.id}`,
      sourceId: todo.id,
      type: "todo",
      title: todo.title,
      date: todo.dueDate,
      projectId: todo.projectId,
    });
  }

  for (const permit of sources.permits ?? []) {
    if (permit.status === "Closed" || permit.status === "Approved" || isDeleted(permit)) {
      continue;
    }

    for (const [field, date] of [
      ["submission", permit.submissionDueDate],
      ["approval", permit.approvalDeadline],
    ] as const) {
      if (!date || !isDueOrOverdue(date, today)) {
        continue;
      }

      items.push({
        id: `permit:${field}:${permit.id}`,
        sourceId: permit.id,
        type: "permit",
        title: permit.title,
        date,
        projectId: permit.projectId,
      });
    }
  }

  for (const interaction of sources.interactions ?? []) {
    if (interaction.status !== "Scheduled" || isDeleted(interaction) || !isDueOrOverdue(interaction.startsAt, today)) {
      continue;
    }

    items.push({
      id: `interaction:${interaction.id}`,
      sourceId: interaction.id,
      type: "interaction",
      title: interaction.title,
      date: interaction.startsAt,
      projectId: interaction.projectId,
    });
  }

  for (const invoice of sources.invoices ?? []) {
    if (
      !invoice.dueDate ||
      invoice.status === "Paid" ||
      invoice.status === "Cancelled" ||
      isDeleted(invoice) ||
      !isDueOrOverdue(invoice.dueDate, today)
    ) {
      continue;
    }

    items.push({
      id: `invoice:${invoice.id}`,
      sourceId: invoice.id,
      type: "invoice",
      title: invoice.title,
      date: invoice.dueDate,
      projectId: invoice.projectId,
    });
  }

  for (const maintenance of sources.maintenance ?? []) {
    if (
      !maintenance.nextDueDate ||
      maintenance.status === "Complete" ||
      isDeleted(maintenance) ||
      !isDueOrOverdue(maintenance.nextDueDate, today)
    ) {
      continue;
    }

    items.push({
      id: `maintenance:${maintenance.id}`,
      sourceId: maintenance.id,
      type: "maintenance",
      title: maintenance.title,
      date: maintenance.nextDueDate,
    });
  }

  return items.sort(compareDatedItems);
}

function isInRange(date: string, range: DateRange): boolean {
  const day = localDate(date);
  return day >= range.from && day <= range.to;
}

export function getScheduleItems(sources: HouseholdScheduleSources, range: DateRange): DatedHouseholdItem[] {
  const items: DatedHouseholdItem[] = [];

  for (const todo of sources.todos ?? []) {
    if (!todo.dueDate || todo.status === "Done" || isDeleted(todo) || !isInRange(todo.dueDate, range)) {
      continue;
    }

    items.push({
      id: `todo:${todo.id}`,
      sourceId: todo.id,
      type: "todo",
      title: todo.title,
      date: todo.dueDate,
      projectId: todo.projectId,
    });
  }

  for (const permit of sources.permits ?? []) {
    if (permit.status === "Closed" || isDeleted(permit)) {
      continue;
    }

    for (const [field, date] of [
      ["submission", permit.submissionDueDate],
      ["approval", permit.approvalDeadline],
    ] as const) {
      if (!date || !isInRange(date, range)) {
        continue;
      }

      items.push({
        id: `permit:${field}:${permit.id}`,
        sourceId: permit.id,
        type: "permit",
        title: permit.title,
        date,
        projectId: permit.projectId,
      });
    }
  }

  for (const interaction of sources.interactions ?? []) {
    if (interaction.status === "Cancelled" || isDeleted(interaction) || !isInRange(interaction.startsAt, range)) {
      continue;
    }

    items.push({
      id: `interaction:${interaction.id}`,
      sourceId: interaction.id,
      type: "interaction",
      title: interaction.title,
      date: interaction.startsAt,
      projectId: interaction.projectId,
    });
  }

  for (const invoice of sources.invoices ?? []) {
    if (!invoice.dueDate || invoice.status === "Cancelled" || isDeleted(invoice) || !isInRange(invoice.dueDate, range)) {
      continue;
    }

    items.push({
      id: `invoice:${invoice.id}`,
      sourceId: invoice.id,
      type: "invoice",
      title: invoice.title,
      date: invoice.dueDate,
      projectId: invoice.projectId,
    });
  }

  for (const maintenance of sources.maintenance ?? []) {
    if (
      !maintenance.nextDueDate ||
      maintenance.status === "Complete" ||
      isDeleted(maintenance) ||
      !isInRange(maintenance.nextDueDate, range)
    ) {
      continue;
    }

    items.push({
      id: `maintenance:${maintenance.id}`,
      sourceId: maintenance.id,
      type: "maintenance",
      title: maintenance.title,
      date: maintenance.nextDueDate,
    });
  }

  return items.sort(compareDatedItems);
}

function formatDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function addMonths(dateString: string, months: number): string {
  const [year = 0, month = 1, day = 1] = dateString.split("-").map(Number);
  const date = new Date(Date.UTC(year, month - 1 + months, day));
  return formatDate(date);
}

export function calculateNextMaintenanceDueDate(input: MaintenanceRecurrenceInput): string | undefined {
  switch (input.frequency) {
    case "One-time":
      return undefined;
    case "Monthly":
      return addMonths(input.completedOn, 1);
    case "Quarterly":
      return addMonths(input.completedOn, 3);
    case "Yearly":
      return addMonths(input.completedOn, 12);
    case "Custom": {
      if (!input.customIntervalDays || input.customIntervalDays < 1) {
        throw new Error("Custom maintenance recurrence requires a positive day interval.");
      }

      const date = new Date(`${input.completedOn}T00:00:00.000Z`);
      date.setUTCDate(date.getUTCDate() + input.customIntervalDays);
      return formatDate(date);
    }
  }
}

function sumAmounts<T>(records: T[], amountFor: (record: T) => number | undefined): number {
  return records.reduce((total, record) => total + (amountFor(record) ?? 0), 0);
}

export function rollupProjectCosts(input: ProjectCostInput): ProjectCostRollup {
  const supplies = (input.supplies ?? []).filter((supply) => !isDeleted(supply));
  const quotes = (input.quotes ?? []).filter((quote) => !isDeleted(quote) && quote.status !== "Declined" && quote.status !== "Expired");
  const invoices = (input.invoices ?? []).filter((invoice) => !isDeleted(invoice) && invoice.status !== "Cancelled");

  const estimatedSupplyTotal = sumAmounts(supplies, (supply) => supply.estimatedCost);
  const actualSupplyTotal = sumAmounts(supplies, (supply) => supply.actualCost);
  const quoteTotal = sumAmounts(quotes, (quote) => quote.amount);
  const invoiceTotal = sumAmounts(invoices, (invoice) => invoice.amount);
  const actualCost = actualSupplyTotal + invoiceTotal;

  return {
    estimatedSupplyTotal,
    actualSupplyTotal,
    quoteTotal,
    invoiceTotal,
    actualCost,
    variance: input.budget === undefined ? undefined : input.budget - actualCost,
  };
}

const attachmentTargets = new Set<AttachmentTarget>([
  "project",
  "supply",
  "permit",
  "interaction",
  "quote",
  "invoice",
  "maintenance",
]);

function slugSegment(value: string): string {
  const slug = value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9.]+/g, "-")
    .replace(/^-+|-+$/g, "");

  return slug || "attachment";
}

export function createAttachmentStorageKey(input: AttachmentKeyInput): string {
  if (!attachmentTargets.has(input.entityType as AttachmentTarget)) {
    throw new Error("Unsupported attachment target");
  }

  return [
    "households",
    slugSegment(input.householdId),
    slugSegment(input.entityType),
    slugSegment(input.entityId),
    `${input.uniqueId.trim()}-${slugSegment(input.filename)}`,
  ].join("/");
}
